import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { clearSupportEmailAutoSendSession } from '@/hooks/useSupportEmailVerification';
import { useTranslation } from 'react-i18next';
import { EmailVerification } from './email-verification';

type SupportEmailVerificationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  productName?: string;
  verifiedEmail: string | null;
  onVerified: (email: string) => void;
  onInvalidDomain?: () => void;
};

export function SupportEmailVerificationDialog({
  open,
  onOpenChange,
  email,
  productName,
  verifiedEmail,
  onVerified,
  onInvalidDomain,
}: SupportEmailVerificationDialogProps) {
  const { t } = useTranslation(['common', 'onboarding']);
  const [verificationSession, setVerificationSession] = useState(0);
  const isActiveRef = useRef(false);
  const verificationSessionRef = useRef(0);
  const emailRef = useRef(email);

  emailRef.current = email;

  useEffect(() => {
    if (!open) {
      isActiveRef.current = false;
      clearSupportEmailAutoSendSession(
        verificationSessionRef.current,
        emailRef.current,
      );
      return;
    }

    isActiveRef.current = true;
    setVerificationSession((current) => {
      const next = current + 1;
      verificationSessionRef.current = next;
      return next;
    });
  }, [open]);

  const handleClose = () => {
    isActiveRef.current = false;
    onOpenChange(false);
  };

  const handleVerified = (verified: string) => {
    if (!isActiveRef.current) return;
    isActiveRef.current = false;
    onVerified(verified);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogContent
        className="max-w-md border-colorBorder p-0 overflow-hidden"
        data-cy="support-email-verification-dialog"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader className="p-6 pb-2 border-b border-colorBorder">
          <DialogTitle className="text-xl font-semibold">
            {t('common:verifyEmail')}
          </DialogTitle>
          <DialogDescription className="text-left text-muted whitespace-pre-line">
            {t('common:supportEmailVerificationEnterCode')}
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 pt-4">
          {open && verificationSession > 0 && (
            <EmailVerification
              key={verificationSession}
              email={email}
              productName={productName}
              verifiedEmail={verifiedEmail}
              onVerified={handleVerified}
              onInvalidDomain={() => {
                if (!isActiveRef.current) return;
                isActiveRef.current = false;
                onInvalidDomain?.();
                onOpenChange(false);
              }}
              autoSendOnMount
              sendSessionKey={verificationSession}
              enabled={open}
              showSentMessage
              otpDataCyPrefix="edit-product-support-email-otp"
              leadingActions={
                <Button type="button" variant="ghost" onClick={handleClose}>
                  {t('common:cancel')}
                </Button>
              }
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
